/**
 * Provider REAL de la lista de salas (M3): consume el endpoint `GET /rooms` del
 * servidor autoritativo y cumple el mismo `RoomListProvider` que el mock, así
 * la vista del lobby no cambia (ver contract.ts).
 *
 * Sigue el roadmap §1.2: la lista sale por HTTP (cacheable, TTL), no del canal
 * de juego. Reemplaza a `OfflineRoomListProvider` cuando hay un servidor WS real.
 */
import {
  RoomFilter,
  RoomListProvider,
  RoomListSnapshot,
  RoomSummary,
  RegionPing,
  RegionId,
  passesFilter,
} from "./contract";

export class HttpRoomListProvider implements RoomListProvider {
  private readonly roomsUrl: string;

  /** @param httpBase base HTTP del servidor, p.ej. "https://host" (sin `/rooms`). */
  constructor(httpBase: string) {
    this.roomsUrl = httpBase.replace(/\/+$/, "") + "/rooms";
  }

  private async fetchSnapshot(): Promise<RoomListSnapshot> {
    const res = await fetch(this.roomsUrl, { cache: "no-store" });
    if (!res.ok) throw new Error(`GET /rooms → ${res.status}`);
    return (await res.json()) as RoomListSnapshot;
  }

  async listRooms(filter?: RoomFilter): Promise<RoomListSnapshot> {
    let snap: RoomListSnapshot;
    try {
      snap = await this.fetchSnapshot();
    } catch {
      // Servidor caído/inalcanzable: lista vacía en vez de romper la UI.
      return { rooms: [], fetchedAt: Date.now(), ttlMs: 1000 };
    }
    const rooms = filter ? snap.rooms.filter((r) => passesFilter(r, filter)) : snap.rooms;
    return { ...snap, rooms };
  }

  async pingRegions(): Promise<RegionPing[]> {
    // RTT real de aplicación cliente↔servidor (§1.3), medido sobre el propio
    // fetch de la lista; se anota a las regiones que el servidor está sirviendo.
    const t0 = performance.now();
    let snap: RoomListSnapshot;
    try {
      snap = await this.fetchSnapshot();
    } catch {
      return [];
    }
    const rttMs = Math.max(1, Math.round(performance.now() - t0));
    const regions = new Set<RegionId>(snap.rooms.map((r) => r.region));
    return [...regions].map((region) => ({ region, rttMs }));
  }
}

/** Mide el RTT real a un edge con un probe ligero a `/ping` (timeout incluido). */
export async function probeRegion(httpBase: string, timeoutMs = 2500): Promise<number | null> {
  const url = httpBase.replace(/\/+$/, "") + "/ping";
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const t0 = performance.now();
  try {
    const res = await fetch(url, { cache: "no-store", signal: ctrl.signal });
    if (!res.ok) return null;
    await res.text();
    return Math.max(1, Math.round(performance.now() - t0));
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Provider REAL multi-región (TAREA 3): agrega la lista de salas de VARIOS edges
 * (uno por región) y mide el ping REAL a cada uno. Cada sala se etiqueta con la
 * región del edge que la sirve, así el join sabe a qué `wss://` conectarse.
 */
export class MultiRegionRoomListProvider implements RoomListProvider {
  /** @param edges RegionId → base HTTP de su edge (p.ej. "https://eu.example"). */
  constructor(private readonly edges: Partial<Record<RegionId, string>>) {}

  private entries(): [RegionId, string][] {
    return Object.entries(this.edges) as [RegionId, string][];
  }

  async listRooms(filter?: RoomFilter): Promise<RoomListSnapshot> {
    // Si el filtro fija una región, solo consultamos ese edge (menos tráfico).
    const targets = filter?.region
      ? this.entries().filter(([region]) => region === filter.region)
      : this.entries();

    const results = await Promise.all(targets.map(async ([region, base]) => {
      try {
        const res = await fetch(base.replace(/\/+$/, "") + "/rooms", { cache: "no-store" });
        if (!res.ok) return [] as RoomSummary[];
        const snap = (await res.json()) as RoomListSnapshot;
        // La región AUTORITATIVA es la del edge consultado (no lo que diga el JSON).
        return snap.rooms.map((r) => ({ ...r, region }));
      } catch {
        return [] as RoomSummary[];
      }
    }));

    let rooms = results.flat();
    if (filter) rooms = rooms.filter((r) => passesFilter(r, filter));
    return { rooms, fetchedAt: Date.now(), ttlMs: 2000 };
  }

  async pingRegions(): Promise<RegionPing[]> {
    const pings = await Promise.all(this.entries().map(async ([region, base]) => {
      const rttMs = await probeRegion(base);
      return rttMs == null ? null : { region, rttMs };
    }));
    return pings.filter((p): p is RegionPing => p !== null);
  }
}
